import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { enqueueSnackbar } from "notistack";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  getAssetPricingFormulas,
  updateAssetPricingFormula,
  exportAssetPricingFormulas,
} from "../../services/PricingService";
import { hasRoleCode } from "../../helpers/accessControl";
import "../Pricing/PricingDashboard.css";

const parsePayload = (response) => response?.data?.data ?? [];

const formatPercent = (value) => {
  const number = Number(value || 0) * 100;
  if (!Number.isFinite(number)) return "0";
  return Number(number.toFixed(4)).toString();
};

const fromPercent = (value) => {
  const number = Number(String(value ?? "").replace(/,/g, ""));
  if (!Number.isFinite(number)) return 0;
  return number / 100;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return number.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const PricingFormulas = () => {
  document.title = "Pricing Formulas | Valitan";
  const isPricingReadOnly = hasRoleCode("PRICING");
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingId, setSavingId] = useState("");

  const loadFormulas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAssetPricingFormulas();
      const data = parsePayload(response);
      setFormulas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load pricing formulas:", error);
      enqueueSnackbar("Failed to load pricing formulas.", { variant: "error" });
      setFormulas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFormulas();
  }, [loadFormulas]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportAssetPricingFormulas();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "pricing_formulas.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      enqueueSnackbar("Export downloaded successfully.", { variant: "success" });
    } catch (error) {
      console.error("Failed to export formulas:", error);
      enqueueSnackbar("Failed to export formulas.", { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const updateRow = (id, key, value) => {
    setFormulas((current) => current.map((item) => (
      item.id === id ? { ...item, [key]: value } : item
    )));
  };

  const saveFormula = async (formula) => {
    if (isPricingReadOnly) {
      enqueueSnackbar("Pricing formulas are read-only for this role.", { variant: "info" });
      return;
    }

    setSavingId(formula.id);
    try {
      const acquisitionStartYear = formula.acquisition_start_year === "" ? null : Number(formula.acquisition_start_year);
      const acquisitionEndYear = formula.acquisition_end_year === "" ? null : Number(formula.acquisition_end_year);
      if ((acquisitionStartYear === null) !== (acquisitionEndYear === null)) {
        enqueueSnackbar("Set both acquisition start year and end year, or leave both blank.", { variant: "warning" });
        return;
      }
      if (acquisitionStartYear !== null && acquisitionStartYear > acquisitionEndYear) {
        enqueueSnackbar("Acquisition start year must be less than or equal to end year.", { variant: "warning" });
        return;
      }

      const payload = {
        useful_life_years: formula.useful_life_years === "" ? null : Number(formula.useful_life_years),
        min_base_amount: formula.min_base_amount === "" ? null : Number(String(formula.min_base_amount).replace(/,/g, "")),
        max_base_amount: formula.max_base_amount === "" ? null : Number(String(formula.max_base_amount).replace(/,/g, "")),
        depreciation_rate: fromPercent(formula.depreciation_percent),
        acquisition_start_year: acquisitionStartYear,
        acquisition_end_year: acquisitionEndYear,
        active: formula.active !== false,
      };
      await updateAssetPricingFormula(formula.id, payload);
      enqueueSnackbar("Pricing formula updated.", { variant: "success" });
      await loadFormulas();
    } catch (error) {
      console.error("Failed to update pricing formula:", error);
      enqueueSnackbar(error?.response?.data?.message || "Failed to update pricing formula.", { variant: "error" });
    } finally {
      setSavingId("");
    }
  };

  const rows = formulas.map((item) => ({
    ...item,
    depreciation_percent: item.depreciation_percent ?? formatPercent(item.depreciation_rate ?? item.depreciationRate),
    useful_life_years: item.useful_life_years ?? item.usefulLifeYears ?? "",
    min_base_amount: item.min_base_amount ?? item.minBaseAmount ?? "",
    max_base_amount: item.max_base_amount ?? item.maxBaseAmount ?? "",
    replacement_factor: item.replacement_factor ?? item.replacementFactor ?? "",
    acquisition_start_year: item.acquisition_start_year ?? item.acquisitionStartYear ?? "",
    acquisition_end_year: item.acquisition_end_year ?? item.acquisitionEndYear ?? "",
  }));

  return (
    <div className="page-content pricing-dashboard">
      <Container fluid>
        <Breadcrumbs title="Asset Valuation" breadcrumbItem="Pricing Formulas" />

        <Card className="pricing-card">
          <CardHeader>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h4 className="card-title mb-1">Pricing Formula Setup</h4>
                <p className="text-muted mb-0">
                  Condition is read automatically from each asset during pricing. Acquisition year settings define the randomized acquisition date range.
                </p>
              </div>
              <Button color="success" size="sm" onClick={handleExport} disabled={exporting || loading}>
                {exporting ? <><Spinner size="sm" className="me-2" /> Exporting...</> : <><i className="fas fa-file-excel me-1" /> Export to Excel</>}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : rows.length === 0 ? (
              <Alert color="warning" className="mb-0">No pricing formulas are configured.</Alert>
            ) : (
              <div className="pricing-table-wrap">
                <Table bordered hover responsive className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Category Code</th>
                      <th>Category</th>
                      <th>Condition</th>
                      <th>Useful Life</th>
                      <th>Min Amount</th>
                      <th>Max Amount</th>
                      <th>Depreciation %</th>
                      <th>Replacement Factor</th>
                      <th>Acq. Start Year</th>
                      <th>Acq. End Year</th>
                      <th style={{ width: 120 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((formula) => (
                      <tr key={formula.id}>
                        <td>{formula.category_code || formula.categoryCode || "-"}</td>
                        <td>{formula.category_name || formula.categoryName || "-"}</td>
                        <td>{formula.condition_name || formula.conditionName || "-"}</td>
                        <td>
                          <Input
                            bsSize="sm"
                            value={formula.useful_life_years}
                            readOnly={isPricingReadOnly}
                            disabled={isPricingReadOnly}
                            onChange={(event) => updateRow(formula.id, "useful_life_years", event.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            value={formula.min_base_amount}
                            placeholder="No minimum"
                            readOnly={isPricingReadOnly}
                            disabled={isPricingReadOnly}
                            onChange={(event) => updateRow(formula.id, "min_base_amount", event.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            value={formula.max_base_amount}
                            placeholder="No maximum"
                            readOnly={isPricingReadOnly}
                            disabled={isPricingReadOnly}
                            onChange={(event) => updateRow(formula.id, "max_base_amount", event.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            value={formula.depreciation_percent}
                            readOnly={isPricingReadOnly}
                            disabled={isPricingReadOnly}
                            onChange={(event) => updateRow(formula.id, "depreciation_percent", event.target.value)}
                          />
                        </td>
                        <td>{formatNumber(formula.replacement_factor)}</td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            value={formula.acquisition_start_year}
                            placeholder="Not set"
                            readOnly={isPricingReadOnly}
                            disabled={isPricingReadOnly}
                            onChange={(event) => updateRow(formula.id, "acquisition_start_year", event.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            value={formula.acquisition_end_year}
                            placeholder="Not set"
                            readOnly={isPricingReadOnly}
                            disabled={isPricingReadOnly}
                            onChange={(event) => updateRow(formula.id, "acquisition_end_year", event.target.value)}
                          />
                        </td>
                        <td>
                          <Button
                            color="primary"
                            size="sm"
                            disabled={isPricingReadOnly || savingId === formula.id}
                            onClick={() => saveFormula(formula)}
                          >
                            {isPricingReadOnly ? "Read Only" : savingId === formula.id ? "Saving..." : "Save"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <Row className="mt-3">
          <Col>
            <Alert color="info" className="mb-0">
              Example: if entered amount is TZS 10,000,000 and condition depreciation is 25%, accumulated depreciation is TZS 2,500,000. If acquisition range is 2017-2023, the saved acquisition date is randomized within those years.
            </Alert>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PricingFormulas;
